import React from 'react';
import L from 'leaflet';
import 'leaflet-draw';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import type { SatOccupancy } from '../api';
import { RADIUS, TYPE, useTheme } from '../theme';
import { Button, Icon, IconButton, Input } from '../components/ui';
import { busyBySat, occupancyLabel, parseSatar, removeSat, satColor, satInsideBoundary, SAT_BUSY_COLOR, upsertSat, type SatShape } from './satar';

// S1 · Såter på jaktmarkens karta: lista med dagens beläggning, och för den som får ändra marken
// rita ny såt, döp om, ändra form och ta bort. Såterna sparas som delområden (sub_area) i
// markgränsen via onSave (samma väg som när gränsen ritas).

type Props = {
  map: L.Map | null;
  /** Senast kända gränsdata (FeatureCollection som text). */
  coordinatesJson: string | null | undefined;
  occupancy: SatOccupancy[];
  canEdit: boolean;
  onSave: (coordinatesJson: string) => Promise<void>;
  onClose: () => void;
  mobile: boolean;
};

type Edit =
  | { kind: 'draw' }
  | { kind: 'new'; geometry: SatShape['geometry'] }
  | { kind: 'rename'; id: string }
  | { kind: 'shape'; id: string };

export function SatPanel({ map, coordinatesJson, occupancy, canEdit, onSave, onClose, mobile }: Props) {
  const T = useTheme();
  const satar = React.useMemo(() => parseSatar(coordinatesJson), [coordinatesJson]);
  const busy = React.useMemo(() => busyBySat(satar, occupancy), [satar, occupancy]);
  const [edit, setEdit] = React.useState<Edit | null>(null);
  const [name, setName] = React.useState('');
  const [busySave, setBusySave] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [confirmDel, setConfirmDel] = React.useState<string | null>(null);
  const tmp = React.useRef<L.Layer | null>(null);
  const drawer = React.useRef<any>(null);

  const clearTmp = React.useCallback(() => {
    try { drawer.current?.disable(); } catch { /* */ }
    drawer.current = null;
    if (tmp.current && map) { try { (tmp.current as any).editing?.disable(); } catch { /* */ } map.removeLayer(tmp.current); }
    tmp.current = null;
  }, [map]);
  React.useEffect(() => () => clearTmp(), [clearTmp]);

  const cancel = () => { clearTmp(); setEdit(null); setErr(null); };

  const startDraw = () => {
    if (!map) return;
    cancel();
    setEdit({ kind: 'draw' });
    setName(`Såt ${satar.length + 1}`);
    const d = new (L as any).Draw.Polygon(map, { shapeOptions: { color: satColor(satar.length), weight: 3, fillOpacity: 0.15 } });
    drawer.current = d;
    d.enable();
    map.once('draw:created', (e: any) => {
      drawer.current = null;
      tmp.current = e.layer;
      map.addLayer(e.layer);
      setEdit({ kind: 'new', geometry: e.layer.toGeoJSON().geometry });
    });
    map.once('draw:drawstop', () => setEdit(cur => (cur?.kind === 'draw' ? null : cur)));
  };

  const startShape = (s: SatShape) => {
    if (!map) return;
    cancel();
    const layer = L.geoJSON({ type: 'Feature', properties: {}, geometry: s.geometry } as any).getLayers()[0] as any;
    layer.setStyle?.({ color: T.amber, weight: 3, fillOpacity: 0.15, dashArray: undefined });
    map.addLayer(layer);
    try { layer.editing?.enable(); } catch { /* leaflet-draw saknas */ }
    tmp.current = layer;
    try { map.fitBounds(layer.getBounds(), { padding: [40, 40] }); } catch { /* */ }
    setEdit({ kind: 'shape', id: s.id });
  };

  const save = async (json: string) => {
    setBusySave(true); setErr(null);
    try { await onSave(json); clearTmp(); setEdit(null); setConfirmDel(null); }
    catch (e) { setErr((e as Error).message || 'Kunde inte spara'); }
    setBusySave(false);
  };

  const outside = (g: SatShape['geometry']) => {
    if (satInsideBoundary(coordinatesJson, g)) return false;
    setErr('Såten ligger utanför markgränsen. Rita den inom marken.');
    return true;
  };
  const saveNew = () => edit?.kind === 'new' && !outside(edit.geometry) && save(upsertSat(coordinatesJson, { name: name.trim() || `Såt ${satar.length + 1}`, geometry: edit.geometry }));
  const saveRename = (id: string) => save(upsertSat(coordinatesJson, { id, name }));
  const saveShape = (s: SatShape) => {
    const geometry = (tmp.current as any)?.toGeoJSON?.().geometry;
    if (!geometry || outside(geometry)) return;
    void save(upsertSat(coordinatesJson, { id: s.id, name: s.name, geometry }));
  };

  const box: React.CSSProperties = mobile
    ? { position: 'fixed', left: 0, right: 0, bottom: 0, maxHeight: '62vh', borderRadius: `${RADIUS.lg}px ${RADIUS.lg}px 0 0`, paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }
    : { position: 'fixed', top: 'calc(72px + env(safe-area-inset-top, 0px))', right: 16, width: 340, maxHeight: 'calc(100vh - 160px)', borderRadius: RADIUS.lg };

  return (
    <div role="dialog" aria-label="Såter" data-testid="sat-panel"
      style={{ ...box, zIndex: 9996, background: T.surface, color: T.text, boxShadow: T.shadow.float, overflowY: 'auto', padding: 14, boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ ...TYPE.title, flex: 1 }}>Såter</div>
        <IconButton label="Stäng såter" onClick={() => { cancel(); onClose(); }}><X size={20} strokeWidth={2} /></IconButton>
      </div>
      <div style={{ ...TYPE.small, color: T.textMuted, marginBottom: 10 }}>
        {satar.length ? 'Röd = bokad eller jakt pågår i dag. Jägare i olika såter ser varandras områden.' : 'Marken har inga såter än.'}
        {canEdit && ' En såt kan bokas för sig utan att blockera de andra.'}
      </div>

      {satar.map((s, i) => {
        const b = busy.get(s.id) ?? [];
        const renaming = edit?.kind === 'rename' && edit.id === s.id;
        const shaping = edit?.kind === 'shape' && edit.id === s.id;
        return (
          <div key={s.id} data-testid="sat-row" style={{ borderTop: `1px solid ${T.border}`, padding: '8px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44 }}>
              <span aria-hidden style={{ width: 14, height: 14, borderRadius: 4, flexShrink: 0, background: b.length ? SAT_BUSY_COLOR : satColor(i) }} />
              {renaming ? (
                <Input aria-label="Nytt namn på såten" value={name} autoFocus onChange={e => setName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') void saveRename(s.id); if (e.key === 'Escape') cancel(); }} style={{ flex: 1 }} />
              ) : (
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ ...TYPE.body, fontWeight: 700 }}>{s.name}</div>
                  <div style={{ ...TYPE.small, color: b.length ? SAT_BUSY_COLOR : T.textMuted }}>{b.length ? b.map(occupancyLabel).join(' · ') : 'Ledig i dag'}</div>
                </div>
              )}
              {canEdit && !renaming && !shaping && (
                <>
                  <IconButton label={`Döp om ${s.name}`} onClick={() => { cancel(); setName(s.name); setEdit({ kind: 'rename', id: s.id }); }}><Pencil size={18} strokeWidth={2} /></IconButton>
                  <IconButton label={`Ta bort ${s.name}`} onClick={() => setConfirmDel(s.id)}><Trash2 size={18} strokeWidth={2} color={T.danger} /></IconButton>
                </>
              )}
            </div>
            {renaming && (
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <Button size="sm" onClick={() => void saveRename(s.id)} loading={busySave}>Spara namn</Button>
                <Button size="sm" variant="ghost" onClick={cancel}>Avbryt</Button>
              </div>
            )}
            {canEdit && !renaming && !shaping && (
              <Button size="sm" variant="ghost" onClick={() => startShape(s)} style={{ marginTop: 2 }}>Ändra form på kartan</Button>
            )}
            {shaping && (
              <div style={{ marginTop: 6 }}>
                <div style={{ ...TYPE.small, color: T.textSub, marginBottom: 6 }}>Dra i hörnen på kartan. Spara när du är klar.</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="sm" onClick={() => saveShape(s)} loading={busySave}>Spara form</Button>
                  <Button size="sm" variant="ghost" onClick={cancel}>Avbryt</Button>
                </div>
              </div>
            )}
            {confirmDel === s.id && (
              <div role="alert" style={{ marginTop: 6, padding: 10, borderRadius: RADIUS.md, background: T.surface2 }}>
                <div style={{ ...TYPE.small, marginBottom: 6 }}>Ta bort {s.name}? Planerade jakter och annonser som gäller såten räknas då inte längre till den.</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="sm" variant="danger" onClick={() => void save(removeSat(coordinatesJson, s.id))} loading={busySave}>Ta bort</Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmDel(null)}>Avbryt</Button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {canEdit && (
        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
          {edit?.kind === 'draw' && <div role="status" style={{ ...TYPE.small, color: T.textSub }}>Tryck ut hörnen på kartan och avsluta på första punkten.</div>}
          {edit?.kind === 'new' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Input aria-label="Namn på den nya såten" value={name} autoFocus onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') void saveNew(); }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <Button size="sm" onClick={() => void saveNew()} loading={busySave}>Spara såten</Button>
                <Button size="sm" variant="ghost" onClick={cancel}>Avbryt</Button>
              </div>
            </div>
          )}
          {!edit || edit.kind === 'rename' || edit.kind === 'shape' ? (
            <Button variant="secondary" icon={<Icon as={Plus} size={18} />} onClick={startDraw} disabled={!map || !!edit}>Rita ny såt</Button>
          ) : edit.kind === 'draw' ? <Button size="sm" variant="ghost" onClick={cancel} style={{ marginTop: 6 }}>Avbryt</Button> : null}
        </div>
      )}
      {err && <div role="alert" style={{ color: T.danger, ...TYPE.small, fontWeight: 600, marginTop: 8 }}>{err}</div>}
    </div>
  );
}
